/**
 * Grammar topic detail page component.
 *
 * Displays detailed grammar lesson content and interactive quiz for a specific
 * grammar topic. Provides tabbed interface for switching between lesson and
 * quiz, bookmarking functionality, and progress tracking.
 *
 * Route: `/learning/grammar/:level/:topicId`
 * Requires authentication: Yes
 *
 * @module GrammarTopicDetail
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronRight, Bookmark, BookmarkCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { grammarLevels } from "@/data/learningData";
import { toast } from "@/components/ui/sonner";
import { loadGrammarBookmarks, saveGrammarBookmarks } from "@/lib/storage";
import { GrammarLesson } from "@/components/learning/GrammarLesson";
import { GrammarQuiz as GrammarQuizComponent } from "@/components/learning/GrammarQuiz";
import { useGrammarContent } from "@/hooks/useContentLoader";
import { ROUTES } from "@/lib/routeConstants";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StandardPageShell } from "@/components/layout/StandardPageShell";

const SHELL_MAIN_CLASS =
  "mx-auto w-full max-w-[1200px] flex-1 px-3 py-4 sm:px-6 sm:py-6 lg:px-8";

export default function GrammarTopicDetail() {
  const { t } = useTranslation();
  const { level, topicId } = useParams<{ level: string; topicId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("lesson");
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [lessonCompleted, setLessonCompleted] = useState(false);

  // Redirect if level is locked
  useEffect(() => {
    const lockedLevels = ["A2", "B1", "B2", "C1", "C2"];
    if (level && lockedLevels.includes(level)) {
      toast.error(
        t("learning.comingSoon", {
          defaultValue: "This level is coming soon!",
        }),
      );
      navigate(ROUTES.LEARNING_GRAMMAR);
    }
  }, [level, navigate, t]);

  // Find topic from grammarLevels
  const topic = grammarLevels
    .find((l) => l.level === level)
    ?.topics.find((tp) => tp.id === topicId);

  // Load content using React Query hook with caching
  const {
    data: grammarContent,
    isLoading: loading,
    error,
  } = useGrammarContent(level, topicId);

  // Extract content and quiz from loaded data
  const content = grammarContent?.content || null;
  const quiz = grammarContent?.quiz || null;

  // Load bookmarks
  useEffect(() => {
    const bookmarks = loadGrammarBookmarks();
    setIsBookmarked(bookmarks.has(topicId || ""));
  }, [topicId]);

  // Show error toast if content loading failed
  useEffect(() => {
    if (error && level && topicId) {
      toast.error("Failed to load grammar content");
    }
  }, [error, level, topicId]);

  const handleBookmarkToggle = () => {
    const bookmarks = loadGrammarBookmarks();
    if (bookmarks.has(topicId || "")) {
      bookmarks.delete(topicId || "");
    } else {
      bookmarks.add(topicId || "");
    }
    saveGrammarBookmarks(bookmarks);
    setIsBookmarked(bookmarks.has(topicId || ""));
  };

  const grammarTitle = t("learning.grammar.title", { defaultValue: "Grammar" });

  if (!topic) {
    return (
      <StandardPageShell
        title={grammarTitle}
        headerRight={<LanguageSwitcher />}
        className={SHELL_MAIN_CLASS}
      >
        <div className="py-8 text-center">
          <p className="font-ui text-muted-foreground">Topic not found</p>
          <Button
            onClick={() => navigate(ROUTES.LEARNING_GRAMMAR)}
            className="mt-4"
          >
            Back to Grammar
          </Button>
        </div>
      </StandardPageShell>
    );
  }

  if (loading) {
    return (
      <StandardPageShell
        title={topic.title}
        headerRight={<LanguageSwitcher />}
        className={SHELL_MAIN_CLASS}
      >
        <p className="font-ui text-center text-muted-foreground">
          Loading content...
        </p>
      </StandardPageShell>
    );
  }

  return (
    <StandardPageShell
      title={topic.title}
      headerRight={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleBookmarkToggle}
            className="p-2.5 hover:bg-accent rounded-lg transition-colors border border-border sm:border-0"
            aria-label="Bookmark"
          >
            {isBookmarked ? (
              <BookmarkCheck className="h-5 w-5 text-primary" />
            ) : (
              <Bookmark className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
          <LanguageSwitcher />
        </div>
      }
      className={SHELL_MAIN_CLASS}
    >
      <div>
        {/* Breadcrumbs */}
        <div className="mb-4 sm:mb-6">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground overflow-x-auto whitespace-nowrap pb-2 scrollbar-hide">
            <Link
              to={ROUTES.LEARNING_GRAMMAR}
              className="hover:text-foreground shrink-0"
            >
              {t("learning.title", { defaultValue: "Learning" })}
            </Link>
            <ChevronRight className="h-4 w-4 shrink-0" />
            <Link
              to={ROUTES.LEARNING_GRAMMAR}
              className="hover:text-foreground shrink-0"
            >
              {grammarTitle}
            </Link>
            <ChevronRight className="h-4 w-4 shrink-0" />
            <span className="text-foreground shrink-0">{level}</span>
            <ChevronRight className="h-4 w-4 shrink-0" />
            <span className="text-foreground truncate">{topic.title}</span>
          </nav>
        </div>

        {/* Description — page title is in StandardPageShell h1 */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
          <p className="font-ui text-sm text-muted-foreground flex-1">
            {topic.description}
          </p>
          {lessonCompleted && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none shrink-0"
            >
              {t("learning.markComplete", { defaultValue: "Mark as complete" })}
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 w-full sm:w-auto h-auto p-1 bg-muted/50">
            <TabsTrigger
              value="lesson"
              className="flex-1 sm:flex-none py-2 px-4"
            >
              {t("learning.lesson", { defaultValue: "Lesson" })}
            </TabsTrigger>
            {quiz && (
              <TabsTrigger
                value="quiz"
                className="flex-1 sm:flex-none py-2 px-4"
              >
                {t("learning.quiz", { defaultValue: "Practice" })}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="lesson">
            <GrammarLesson
              content={content}
              activeTab={activeTab}
              onLessonCompleted={() => setLessonCompleted(true)}
            />
          </TabsContent>

          {quiz && (
            <TabsContent value="quiz">
              <GrammarQuizComponent
                quiz={quiz}
                level={level || ""}
                topicId={topicId || ""}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </StandardPageShell>
  );
}
